import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, output } from '@angular/core';
import { LimitInformation } from '@simOn/ui/sim-data-usage';
import { SimIconModule } from '@simOn/ui/sim-icon';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { AGENT_ACCESS_TEXTS, AgentAssistantState, AgentUserStatusDto, formatTokenCount } from '../../model';

interface StatusBlock {
  icon: string;
  title: string;
  description: string;
  modifier: 'ok' | 'warn' | 'off';
}

interface TokenPool {
  label: string;
  /** Right of the pool title ("No expiry"); `null` when there is nothing to say. */
  note: string | null;
  /** "1.61M of 2M left", or "Unlimited". */
  left: string;
  /** Right of the bar ("387K used", "Used after the monthly pool"). */
  aside: string;
  /** Share left, 0-100; `null` hides the bar (unlimited or empty pool). */
  percentLeft: number | null;
  modifier: 'monthly' | 'purchased';
}

/**
 * Access & tokens view of the panel: whether the assistant is enabled, the tokens left in both pools, Refresh,
 * and the Simlab office contact. Everything is managed by the office, so the view is read-only.
 */
@Component({
  selector: 'ai-agent-access-settings',
  imports: [DatePipe, SimIconModule, SimSpinnerComponent],
  templateUrl: './agent-access-settings.component.html',
  styleUrl: './agent-access-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgentAccessSettingsComponent {
  private readonly _locale = inject(LOCALE_ID);

  /** `null` until `GetUserStatus` is loaded. */
  readonly status = input.required<AgentUserStatusDto | null>();
  readonly state = input.required<AgentAssistantState | null>();
  readonly contactEmail = input.required<string>();
  /** E-mail of the signed-in user; `null` when unknown. */
  readonly userEmail = input<string | null>(null);
  /** When the status was last fetched (ISO). */
  readonly checkedAt = input<string | null>(null);
  readonly loading = input<boolean>(false);
  readonly refresh = output<void>();

  private readonly _texts = {
    enabled: $localize`:@@AI_AGENT_ENABLED_TITLE:Assistant is enabled for you`,
    scope: $localize`:@@AI_AGENT_ENABLED_SCOPE:Works in the space you have selected`,
    monthly: $localize`:@@AI_AGENT_MONTHLY_POOL:Monthly pool`,
    purchased: $localize`:@@AI_AGENT_PURCHASED_TOKENS:Purchased tokens`,
    noExpiry: $localize`:@@AI_AGENT_NO_EXPIRY:No expiry`,
    unlimited: $localize`:@@AI_AGENT_UNLIMITED:Unlimited`,
    usedAfterMonthly: $localize`:@@AI_AGENT_USED_AFTER_MONTHLY:Used after the monthly pool`,
    left: (left: string, limit: string) => $localize`:@@AI_AGENT_POOL_LEFT:${left}:left: of ${limit}:limit: left`,
    used: (used: string) => $localize`:@@AI_AGENT_POOL_USED:${used}:used: used`
  };

  protected readonly statusBlock = computed<StatusBlock | null>(() => {
    const state = this.state();
    if (!state) return null;
    if (state === 'ready') {
      const email = this.userEmail();
      return {
        icon: 'check',
        title: this._texts.enabled,
        description: email ? `${this._texts.scope} · ${email}` : this._texts.scope,
        modifier: 'ok'
      };
    }
    const texts = AGENT_ACCESS_TEXTS[state];
    return {
      icon: state === 'noTokens' ? 'error_outline' : 'lock',
      title: texts.title,
      description: texts.description,
      modifier: state === 'noTokens' ? 'warn' : 'off'
    };
  });

  protected readonly totalLeft = computed(() => {
    const status = this.status();
    return status ? formatTokenCount(status.remainingTokens, this._locale) : null;
  });

  /** Monthly tokens are used first, so they come first. */
  protected readonly pools = computed<TokenPool[]>(() => {
    const status = this.status();
    if (!status) return [];
    const monthly = this._pool(status.monthlyTokens);
    const purchased = this._pool(status.purchasedTokens);
    return [
      { label: this._texts.monthly, note: null, ...monthly, aside: monthly.used, modifier: 'monthly' },
      {
        label: this._texts.purchased,
        note: this._texts.noExpiry,
        ...purchased,
        aside: this._texts.usedAfterMonthly,
        modifier: 'purchased'
      }
    ];
  });

  protected readonly mailto = computed(() => `mailto:${this.contactEmail()}`);

  protected onRefresh(): void {
    this.refresh.emit();
  }

  private _pool({
    limit,
    used,
    isUnlimited
  }: LimitInformation): Pick<TokenPool, 'left' | 'percentLeft'> & { used: string } {
    const usedText = this._texts.used(formatTokenCount(used, this._locale));
    if (isUnlimited) return { left: this._texts.unlimited, percentLeft: null, used: usedText };
    const left = Math.max(limit - used, 0);
    return {
      left: this._texts.left(formatTokenCount(left, this._locale), formatTokenCount(limit, this._locale)),
      percentLeft: limit > 0 ? Math.round((left / limit) * 100) : null,
      used: usedText
    };
  }
}
